import type {Suggestion} from '@github-ui/inline-autocomplete/types'
import {ActionList, type ActionListItemProps} from '@primer/react'

/**
 * Sentinel key prefix for empty-state suggestions. `ChatInputAutocomplete`
 * checks for this prefix to short-circuit selection handling (no telemetry,
 * no multistep transition) when an empty-state item is reached via keyboard.
 */
export const EMPTY_STATE_KEY_PREFIX = 'empty-state:'

interface EmptyStateSuggestionProps extends ActionListItemProps {
  leadingVisual: React.ReactNode
  message: string
}

function EmptyStateSuggestion({leadingVisual, message, ...props}: EmptyStateSuggestionProps) {
  return (
    // Override onMouseDown to swallow click selection: the default behavior
    // commits the suggestion, which would hide the menu. We want the menu to
    // stay visible so the user can see the empty-state message.
    <ActionList.Item {...props} disabled onMouseDown={event => event.preventDefault()}>
      <ActionList.LeadingVisual>{leadingVisual}</ActionList.LeadingVisual>
      {message}
    </ActionList.Item>
  )
}

/**
 * Builds a non-selectable suggestion that keeps the autocomplete menu open
 * with helpful empty-state feedback (e.g. "No repositories found") instead of
 * silently closing when a category returns no results.
 */
export function createEmptyStateSuggestion(
  category: string,
  leadingVisual: React.ReactNode,
  message: string,
): Suggestion {
  return {
    value: null,
    key: `${EMPTY_STATE_KEY_PREFIX}${category}`,
    render: props => <EmptyStateSuggestion {...props} leadingVisual={leadingVisual} message={message} />,
  }
}

export function isEmptyStateSuggestionKey(key: string): boolean {
  return key.startsWith(EMPTY_STATE_KEY_PREFIX)
}
